import {
  FEE_PAYMENT_STATUSES,
  PAYMENT_PROVIDERS,
  type FeePaymentStatus,
  type PaymentProvider,
} from "@wallyo/contracts/console/payments";
import { t } from "../../i18n";
import { formatDateTime, formatMoney } from "../../i18n/format";
import { ErrorNotice } from "../../shell/ErrorNotice";
import type { PaymentListState } from "./list-state";
import { PAYMENT_STATUS_TONE, paymentStatusName, providerName } from "./payment-format";
import { paymentsHref, type PaymentFilters } from "./payment-route";

export type PaymentListViewProps = {
  filters: PaymentFilters;
  state: PaymentListState;
  selectedId: string | null;
  onFilters: (filters: PaymentFilters) => void;
  onRetry: () => void;
  onLoadMore: () => void;
};

/** The filters, then the fee payments newest first; a likely double charge stands out. */
export function PaymentListView({ filters, state, selectedId, onFilters, onRetry, onLoadMore }: PaymentListViewProps) {
  return (
    <div className="card payment-list">
      <div className="form queue-filters">
        <div className="field">
          <label htmlFor="payment-provider">{t("payments.filter.provider")}</label>
          <select
            id="payment-provider"
            value={filters.provider ?? ""}
            onChange={(event) =>
              onFilters({ ...filters, provider: (event.target.value || null) as PaymentProvider | null })
            }
          >
            <option value="">{t("payments.filter.all")}</option>
            {PAYMENT_PROVIDERS.map((provider) => (
              <option key={provider} value={provider}>
                {providerName(provider)}
              </option>
            ))}
          </select>
        </div>
        <div className="field">
          <label htmlFor="payment-status">{t("payments.filter.status")}</label>
          <select
            id="payment-status"
            value={filters.status ?? ""}
            onChange={(event) =>
              onFilters({ ...filters, status: (event.target.value || null) as FeePaymentStatus | null })
            }
          >
            <option value="">{t("payments.filter.all")}</option>
            {FEE_PAYMENT_STATUSES.map((status) => (
              <option key={status} value={status}>
                {paymentStatusName(status)}
              </option>
            ))}
          </select>
        </div>
        <div className="field">
          <label htmlFor="payment-from">{t("payments.filter.from")}</label>
          <input
            id="payment-from"
            type="date"
            value={filters.from ?? ""}
            onChange={(event) => onFilters({ ...filters, from: event.target.value || null })}
          />
        </div>
        <div className="field">
          <label htmlFor="payment-to">{t("payments.filter.to")}</label>
          <input
            id="payment-to"
            type="date"
            value={filters.to ?? ""}
            onChange={(event) => onFilters({ ...filters, to: event.target.value || null })}
          />
        </div>
        <label className="check">
          <input
            type="checkbox"
            checked={filters.likelyDuplicates}
            onChange={(event) => onFilters({ ...filters, likelyDuplicates: event.target.checked })}
          />{" "}
          {t("payments.filter.duplicates")}
        </label>
        {filters.workerId ? (
          <button className="btn quiet small" type="button" onClick={() => onFilters({ ...filters, workerId: null })}>
            {t("payments.filter.clearPro")}
          </button>
        ) : null}
      </div>

      {state.status === "error" ? (
        <div className="audit-error">
          <p className="foot">{t("payments.list.loadFailed")}</p>
          <ErrorNotice error={state.error} />
          <div>
            <button className="btn primary" type="button" onClick={onRetry}>
              {t("audit.retry")}
            </button>
          </div>
        </div>
      ) : state.status === "loading" && state.payments.length === 0 ? (
        <div className="empty" role="status">
          <span className="spinner" aria-hidden="true" />
          <span>{t("payments.list.loading")}</span>
        </div>
      ) : state.payments.length === 0 ? (
        <p className="foot" role="status">
          {t("payments.list.empty")}
        </p>
      ) : (
        <div className="rows" aria-label={t("payments.list.aria")}>
          {state.payments.map((payment) => {
            const selected = payment.platformFeePaymentId === selectedId;
            return (
              <a
                key={payment.platformFeePaymentId}
                href={paymentsHref(filters, payment.platformFeePaymentId)}
                className={`verif-row${selected ? " sel" : ""}${payment.likelyDuplicate ? " flagged" : ""}`}
                aria-current={selected ? "true" : undefined}
                data-payment={payment.platformFeePaymentId}
              >
                <span>
                  <b>
                    {payment.pro.name} · {formatMoney(payment.amount, payment.currency)}
                  </b>
                  <br />
                  <span className="meta">
                    {[
                      providerName(payment.provider),
                      t(`payments.feeType.${payment.feeType}` as const),
                      formatDateTime(payment.createdAt),
                    ].join(" · ")}
                  </span>
                </span>
                <span className="verif-pills">
                  {payment.likelyDuplicate ? (
                    <span className="pill bad" data-duplicate="">
                      {t("payments.pill.duplicate")}
                    </span>
                  ) : null}
                  <span className={`pill ${PAYMENT_STATUS_TONE[payment.status]}`}>
                    {paymentStatusName(payment.status)}
                  </span>
                </span>
              </a>
            );
          })}
        </div>
      )}

      {state.status === "ready" && state.payments.length > 0 ? (
        <div className="audit-more">
          {state.moreError ? <ErrorNotice error={state.moreError} /> : null}
          <div className="btns">
            {state.nextCursor ? (
              <button className="btn" type="button" onClick={onLoadMore} disabled={state.loadingMore}>
                {state.loadingMore
                  ? t("customers.search.loadingMore")
                  : state.moreError
                    ? t("customers.search.retry")
                    : t("customers.search.loadMore")}
              </button>
            ) : (
              <span className="meta">{t("payments.list.end")}</span>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}
